/**
 * PrivacyBadge — Trust signals cho commercial mode
 * Dùng ở header, upload screen, processing screen, download screen
 */

import React from 'react';
import { Shield, Lock, Trash2, ShieldCheck, Cloud } from 'lucide-react';
import { isCommercial } from '../../config/appMode';
import { usePrivacyStore } from '../../store/usePrivacyStore';

type BadgeVariant = 'header' | 'upload' | 'processing' | 'download' | 'inline';

interface PrivacyBadgeProps {
  variant?: BadgeVariant;
  className?: string;
}

const MESSAGES: Record<BadgeVariant, { icon: React.ElementType; text: string; subtext?: string }> = {
  header: {
    icon: Lock,
    text: 'Riêng tư',
  },
  upload: {
    icon: Shield,
    text: 'File sẽ tự xóa sau khi dịch xong',
    subtext: 'Chúng tôi không lưu nội dung tài liệu của bạn',
  },
  processing: {
    icon: Lock,
    text: 'Đang xử lý bảo mật',
    subtext: 'Nội dung chỉ tồn tại trong bộ nhớ tạm, không ghi ra đĩa',
  },
  download: {
    icon: Trash2,
    text: 'Đã xóa khỏi server',
    subtext: 'Nội dung tài liệu đã được xóa hoàn toàn',
  },
  inline: {
    icon: ShieldCheck,
    text: 'Bảo mật',
  },
};

const PrivacyBadge: React.FC<PrivacyBadgeProps> = ({ variant = 'header', className = '' }) => {
  if (!isCommercial) return null;

  // US-12-01: header variant reflects current privacy mode dynamically
  const mode = usePrivacyStore(s => s.mode);

  if (variant === 'header') {
    const isIncognito = mode === 'incognito';
    const HeaderIcon = isIncognito ? Shield : Cloud;
    return (
      <div
        className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold ${
          isIncognito ? 'bg-fuchsia-50 text-fuchsia-700' : 'bg-emerald-50 text-emerald-700'
        } ${className}`}
        title={isIncognito ? 'Ẩn Danh: không lưu pending_sync' : 'Cloud: auto-recover nếu sập máy'}
      >
        <HeaderIcon className="w-3 h-3" />
        <span>{isIncognito ? 'Ẩn Danh' : 'Cloud'}</span>
      </div>
    );
  }

  const config = MESSAGES[variant];
  const Icon = config.icon;

  if (variant === 'inline') {
    return (
      <span className={`inline-flex items-center gap-1 text-emerald-600 text-xs font-bold ${className}`}>
        <Icon className="w-3 h-3" />
        {config.text}
      </span>
    );
  }

  return (
    <div className={`flex items-start gap-2.5 p-3 bg-emerald-50/50 rounded-xl border border-emerald-100 ${className}`}>
      <div className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
        <Icon className="w-3.5 h-3.5 text-emerald-600" />
      </div>
      <div>
        <p className="text-sm font-bold text-emerald-800">{config.text}</p>
        {config.subtext && (
          <p className="text-xs text-emerald-600 mt-0.5">{config.subtext}</p>
        )}
      </div>
    </div>
  );
};

export default PrivacyBadge;
